import { useEffect, useState } from 'react';
import { Loader2, MailCheck, MailWarning, Clock, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [weeks, setWeeks] = useState(4);
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [changingPwd, setChangingPwd] = useState(false);

  useEffect(() => {
    api
      .get('/admin/settings')
      .then((res) => {
        setSettings(res.data);
        setWeeks(res.data.auto_hide_sold_weeks);
      })
      .catch(() => toast.error('Failed to load settings'));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', { auto_hide_sold_weeks: parseInt(weeks, 10) });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwd.next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setChangingPwd(true);
    try {
      await api.post('/auth/change-password', {
        current_password: pwd.current,
        new_password: pwd.next,
        confirm_password: pwd.confirm,
      });
      toast.success('Password updated. Use your new password next time you log in.');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.[0]?.msg || 'Failed to change password');
    } finally {
      setChangingPwd(false);
    }
  };

  if (!settings) {
    return (
      <div className="space-y-4 max-w-2xl">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-heading text-3xl tracking-wide text-[#24135F]">SETTINGS</h1>

      <div className="bg-white border border-[#D9D9D6] rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-[#E4002B]/10 flex items-center justify-center">
            <Clock className="h-4 w-4 text-[#E4002B]" />
          </span>
          <div>
            <h2 className="font-heading text-xl tracking-wider text-[#24135F]">SOLD VEHICLE VISIBILITY</h2>
            <p className="text-xs text-[#53565A]/70">Control how long sold vehicles stay on the public website</p>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#53565A]">Auto-hide sold vehicles after (weeks)</Label>
          <div className="flex gap-3 mt-1.5">
            <Input
              data-testid="settings-auto-hide-weeks-input"
              type="number"
              min="0"
              max="52"
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
              className="h-11 rounded-lg w-32 focus-visible:ring-2 focus-visible:ring-[#E4002B]"
            />
            <Button
              data-testid="settings-save-button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-11 px-6 font-bold tracking-wider transition-colors duration-150"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              SAVE
            </Button>
          </div>
          <p className="text-xs text-[#53565A]/70 mt-2">
            Sold vehicles remain visible with a SOLD badge for this many weeks, then are hidden from the public website
            automatically. They always remain visible here in the admin. Set to 0 to hide sold vehicles immediately.
          </p>
        </div>
      </div>

      <div className="bg-white border border-[#D9D9D6] rounded-xl p-6 space-y-4" data-testid="settings-email-card">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-[#E4002B]/10 flex items-center justify-center">
            {settings.email_configured ? (
              <MailCheck className="h-4 w-4 text-[#24135F]" />
            ) : (
              <MailWarning className="h-4 w-4 text-[#53565A]/80" />
            )}
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-xl tracking-wider text-[#24135F]">EMAIL NOTIFICATIONS</h2>
            <p className="text-xs text-[#53565A]/70">Enquiry notifications via Microsoft 365</p>
          </div>
          <Badge
            variant="outline"
            className={
              settings.email_configured
                ? 'bg-[#24135F]/5 text-[#24135F] border-[#24135F]/20'
                : 'bg-[#D9D9D6]/25 text-[#53565A] border-[#53565A]/25'
            }
          >
            {settings.email_configured ? 'Configured' : 'Not configured'}
          </Badge>
        </div>
        <div className="text-sm space-y-2">
          <p className="flex justify-between border-b border-[#D9D9D6]/50 pb-2">
            <span className="text-[#53565A]">Sends from</span>
            <span className="font-medium" data-testid="settings-email-sender">{settings.email_sender || '-'}</span>
          </p>
        </div>
        {settings.email_configured ? (
          <p className="text-xs text-[#53565A]/70">
            Enquiries are emailed automatically from the business Microsoft 365 mailbox. Replying to a notification
            replies directly to the customer.
          </p>
        ) : (
          <div className="bg-[#D9D9D6]/25 border border-[#53565A]/25 rounded-lg p-4 text-xs text-[#53565A] leading-relaxed">
            <strong>Action needed:</strong> The Microsoft 365 connection is not configured on this server. Enquiries
            are still saved here in the admin and marked as <em>queued</em> - nothing is lost. Contact your website
            administrator to add the Microsoft 365 connection details to the server configuration.
          </div>
        )}
      </div>

      <div className="bg-white border border-[#D9D9D6] rounded-xl p-6 space-y-4" data-testid="settings-password-card">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-[#E4002B]/10 flex items-center justify-center">
            <KeyRound className="h-4 w-4 text-[#E4002B]" />
          </span>
          <div>
            <h2 className="font-heading text-xl tracking-wider text-[#24135F]">CHANGE PASSWORD</h2>
            <p className="text-xs text-[#53565A]/70">Update your admin login password</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-[#53565A]">Current password</Label>
            <Input
              data-testid="settings-current-password-input"
              type="password"
              autoComplete="current-password"
              value={pwd.current}
              onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
              className="h-11 rounded-lg mt-1.5 focus-visible:ring-2 focus-visible:ring-[#E4002B]"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">New password</Label>
              <Input
                data-testid="settings-new-password-input"
                type="password"
                autoComplete="new-password"
                value={pwd.next}
                onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                className="h-11 rounded-lg mt-1.5 focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">Confirm new password</Label>
              <Input
                data-testid="settings-confirm-password-input"
                type="password"
                autoComplete="new-password"
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                className="h-11 rounded-lg mt-1.5 focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
          </div>
          <p className="text-xs text-[#53565A]/70">Minimum 8 characters. You will stay logged in - the new password applies from your next login.</p>
          <Button
            data-testid="settings-change-password-button"
            onClick={handleChangePassword}
            disabled={changingPwd || !pwd.current || !pwd.next || !pwd.confirm}
            className="rounded-full bg-[#24135F] text-white hover:bg-[#24135F]/90 h-11 px-6 font-bold tracking-wider transition-colors duration-150"
          >
            {changingPwd && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            UPDATE PASSWORD
          </Button>
        </div>
      </div>
    </div>
  );
}
